import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Box } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { STAFF_SETTINGS_VIEW } from "@/containers/staff/shared/staff-payroll-permissions";
import { hasPermission } from "@/utils/permission-utils";
import {
  toStaffSettingsFormState,
  type StaffSettingsApiPayload,
  type StaffSettingsSetupChecklist,
} from "@/containers/staff/staff-settings/staff-settings-form";
import { checklistIncompleteLabels } from "./payroll-setup-banner";

export type PayrollSetupBannerState = {
  loading: boolean;
  payrollSetupComplete: boolean;
  checklist: StaffSettingsSetupChecklist;
};

export { checklistIncompleteLabels };

/**
 * Loads staff settings once for setup-incomplete banners.
 * Returns complete=true when the user cannot view settings (no banner).
 */
export function usePayrollSetupBanner(): PayrollSetupBannerState {
  const { permissions } = usePermissions();
  const canView =
    hasPermission(permissions, STAFF_SETTINGS_VIEW) ||
    hasPermission(permissions, "employees.view");
  const [state, setState] = useState<PayrollSetupBannerState>({
    loading: canView,
    payrollSetupComplete: true,
    checklist: {
      dayBasisConfigured: true,
      professionalTaxConfigured: true,
      leavePolicyConfigured: true,
    },
  });

  const load = useCallback(() => {
    if (!canView) {
      setState({
        loading: false,
        payrollSetupComplete: true,
        checklist: {
          dayBasisConfigured: true,
          professionalTaxConfigured: true,
          leavePolicyConfigured: true,
        },
      });
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    callApi({ location: "staffSettings", method: "GET" })
      .then((response) => {
        const form = toStaffSettingsFormState(response.data as StaffSettingsApiPayload);
        setState({
          loading: false,
          payrollSetupComplete: form.payrollSetupComplete,
          checklist: form.setupChecklist,
        });
      })
      .catch(() => {
        setState((prev) => ({ ...prev, loading: false }));
      });
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  return state;
}

type StaffPayrollSetupIncompleteBannerProps = {
  /** Compact alert for forms; default is dashboard-style. */
  dense?: boolean;
};

export function StaffPayrollSetupIncompleteBanner({
  dense = false,
}: StaffPayrollSetupIncompleteBannerProps) {
  const { loading, payrollSetupComplete, checklist } = usePayrollSetupBanner();

  if (loading || payrollSetupComplete) {
    return null;
  }

  const missing = checklistIncompleteLabels(checklist);
  const missingText =
    missing.length > 0 ? ` Missing: ${missing.join(" · ")}.` : " Configure Staff settings first.";

  return (
    <Alert
      severity="warning"
      sx={{ mb: dense ? 1.5 : 2 }}
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to="/staffSettings"
          sx={{ whiteSpace: "nowrap" }}
        >
          Staff settings
        </Button>
      }
    >
      <Box component="span">
        Payroll setup is incomplete.{missingText} Office holidays are optional but recommended when
        using working-day payroll.
      </Box>
    </Alert>
  );
}
